import * as React from "react";
import { makeStyles, Theme } from '@material-ui/core/styles';
import {
    List,
    ListItem,
    ListItemText,
    ListItemSecondaryAction,
    IconButton,
    Button,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    TextField,
    Typography,
    Box,
    DialogContentText
} from "@material-ui/core";
import AddIcon from '@material-ui/icons/Add';
import EditIcon from '@material-ui/icons/Edit';
import DeleteIcon from '@material-ui/icons/Delete';
import { useTranslation } from 'react-i18next';
import { v4 as uuidv4 } from 'uuid';
import { Select, IOption } from "../../atoms/forms/Select";
import { WarningMessage } from "../WarningMessage";

export interface IContactPersonData {
    id: string;
    name: string;
    contactPersonTypeStreamId: string | null;
    contactPersonTypeName?: string;
    customTypeName: string | null;
    phoneNumber: string | null;
    emailAddress: string | null;
}

interface IProps {
    contactPersons: IContactPersonData[];
    contactPersonTypes: Array<{ streamId: string; name: string }>;
    loading?: boolean;
    /** When true (editing existing client), shows a save reminder after contact person changes */
    isEditMode?: boolean;
}

interface IDispatch {
    onChange: (contactPersons: IContactPersonData[], isValid: boolean) => void;
}

type AllProps = IProps & IDispatch;

const validateContactPerson = (cp: IContactPersonData, allTypes: Array<{ streamId: string; name: string }>): boolean => {
    // Name required
    if (!cp.name?.trim()) return false;

    // Type required (either predefined or custom)
    const hasType = !!cp.contactPersonTypeStreamId || !!cp.customTypeName?.trim();
    if (!hasType) return false;

    // At least one contact method (OR)
    const hasPhone = !!cp.phoneNumber?.trim();
    const hasEmail = !!cp.emailAddress?.trim();
    if (!hasPhone && !hasEmail) return false;

    return true;
};

export const ContactPersonsList = (props: AllProps) => {
    const classes = useStyles();
    const { t } = useTranslation('client');

    const [dialogOpen, setDialogOpen] = React.useState(false);
    const [deleteDialogOpen, setDeleteDialogOpen] = React.useState(false);
    const [changesCount, setChangesCount] = React.useState(0);
    const [editingIndex, setEditingIndex] = React.useState<number | null>(null);
    const [deleteIndex, setDeleteIndex] = React.useState<number | null>(null);

    const [formData, setFormData] = React.useState<IContactPersonData>({
        id: '',
        name: '',
        contactPersonTypeStreamId: null,
        customTypeName: null,
        phoneNumber: null,
        emailAddress: null
    });

    // Selected type for the Select component (streamId for predefined, or custom name)
    const [selectedType, setSelectedType] = React.useState<IOption | null>(null);

    const handleAdd = () => {
        setEditingIndex(null);
        setFormData({
            id: uuidv4(),
            name: '',
            contactPersonTypeStreamId: null,
            customTypeName: null,
            phoneNumber: null,
            emailAddress: null
        });
        setSelectedType(null);
        setDialogOpen(true);
    };

    const handleEdit = (index: number) => {
        setEditingIndex(index);
        const person = props.contactPersons[index];
        setFormData({ ...person });

        // Set the selected type based on whether it's predefined or custom
        if (person.contactPersonTypeStreamId) {
            const predefinedType = props.contactPersonTypes.find(t => t.streamId === person.contactPersonTypeStreamId);
            setSelectedType(predefinedType ? { value: predefinedType.streamId, label: predefinedType.name } : null);
        } else if (person.customTypeName) {
            setSelectedType({ value: person.customTypeName, label: person.customTypeName });
        } else {
            setSelectedType(null);
        }

        setDialogOpen(true);
    };

    const handleTypeChange = (value: IOption | null) => {
        setSelectedType(value);

        if (!value) {
            setFormData({
                ...formData,
                contactPersonTypeStreamId: null,
                customTypeName: null,
                contactPersonTypeName: undefined
            });
            return;
        }

        // Check if the selected value matches a predefined type
        const predefinedType = props.contactPersonTypes.find(t => t.streamId === value.value || t.name === value.label);

        if (predefinedType) {
            // It's a predefined type
            setFormData({
                ...formData,
                contactPersonTypeStreamId: predefinedType.streamId,
                customTypeName: null,
                contactPersonTypeName: predefinedType.name
            });
        } else {
            // It's a custom type
            setFormData({
                ...formData,
                contactPersonTypeStreamId: null,
                customTypeName: value.label || value.value,
                contactPersonTypeName: undefined
            });
        }
    };

    const handleSave = () => {
        if (!validateContactPerson(formData, props.contactPersonTypes)) {
            return; // Don't save invalid data
        }

        let newList: IContactPersonData[];
        if (editingIndex !== null) {
            // Update existing
            newList = [...props.contactPersons];
            newList[editingIndex] = formData;
        } else {
            // Add new
            newList = [...props.contactPersons, formData];
        }

        const allValid = newList.every(cp => validateContactPerson(cp, props.contactPersonTypes));
        props.onChange(newList, allValid);
        setChangesCount(c => c + 1);
        setDialogOpen(false);
    };

    const handleDeleteClick = (index: number) => {
        setDeleteIndex(index);
        setDeleteDialogOpen(true);
    };

    const handleDeleteConfirm = () => {
        if (deleteIndex !== null) {
            const newList = props.contactPersons.filter((_, i) => i !== deleteIndex);
            const allValid = newList.length === 0 || newList.every(cp => validateContactPerson(cp, props.contactPersonTypes));
            props.onChange(newList, allValid);
            setChangesCount(c => c + 1);
        }
        setDeleteDialogOpen(false);
        setDeleteIndex(null);
    };

    const handleDialogClose = () => {
        setDialogOpen(false);
    };

    const isFormValid = (): boolean => {
        return validateContactPerson(formData, props.contactPersonTypes);
    };

    const getDisplayType = (person: IContactPersonData): string => {
        if (person.contactPersonTypeStreamId) {
            const type = props.contactPersonTypes.find(t => t.streamId === person.contactPersonTypeStreamId);
            return type?.name || person.contactPersonTypeName || '';
        }
        return person.customTypeName || '';
    };

    const getSecondaryText = (person: IContactPersonData): string => {
        const parts: string[] = [];
        if (person.phoneNumber) parts.push(person.phoneNumber);
        if (person.emailAddress) parts.push(person.emailAddress);
        return parts.join(' • ');
    };

    // Convert contact person types to IOption format
    const typeOptions: IOption[] = props.contactPersonTypes.map(type => ({
        value: type.streamId,
        label: type.name
    }));

    return (
        <Box className={classes.root}>
            <Typography variant="h6" gutterBottom>
                {t('contactPersonsTitle')}
            </Typography>

            <List>
                {props.contactPersons.map((person, index) => (
                    <ListItem key={person.id} className={classes.listItem}>
                        <ListItemText
                            primary={`${person.name} (${getDisplayType(person)})`}
                            secondary={getSecondaryText(person)}
                        />
                        <ListItemSecondaryAction>
                            <IconButton
                                edge="end"
                                aria-label="edit"
                                onClick={() => handleEdit(index)}
                                disabled={props.loading}
                            >
                                <EditIcon />
                            </IconButton>
                            <IconButton
                                edge="end"
                                aria-label="delete"
                                onClick={() => handleDeleteClick(index)}
                                disabled={props.loading}
                            >
                                <DeleteIcon />
                            </IconButton>
                        </ListItemSecondaryAction>
                    </ListItem>
                ))}
            </List>

            <Button
                variant="outlined"
                color="primary"
                startIcon={<AddIcon />}
                onClick={handleAdd}
                disabled={props.loading}
                className={classes.addButton}
            >
                {t('contactPersonsAddButton')}
            </Button>

            {/* Add/Edit Dialog */}
            <Dialog
                open={dialogOpen}
                onClose={handleDialogClose}
                maxWidth="sm"
                fullWidth
            >
                <DialogTitle>
                    {editingIndex !== null ? t('contactPersonEditTitle') : t('contactPersonAddTitle')}
                </DialogTitle>
                <DialogContent>
                    <TextField
                        autoFocus
                        margin="dense"
                        label={t('contactPersonNameLabel')}
                        type="text"
                        fullWidth
                        value={formData.name}
                        onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                        required
                    />

                    <Box mt={2}>
                        <Select
                            label={t('contactPersonTypeLabel')}
                            options={typeOptions}
                            value={selectedType}
                            onChange={(value) => handleTypeChange(value)}
                            freeSolo={true}
                            required={true}
                            placeholder={t('contactPersonTypePredefined') + " of typ aangepast type"}
                        />
                    </Box>

                    <TextField
                        margin="dense"
                        label={t('contactPersonPhoneLabel')}
                        type="tel"
                        fullWidth
                        value={formData.phoneNumber || ''}
                        onChange={(e) => setFormData({ ...formData, phoneNumber: e.target.value })}
                    />

                    <TextField
                        margin="dense"
                        label={t('contactPersonEmailLabel')}
                        type="email"
                        fullWidth
                        value={formData.emailAddress || ''}
                        onChange={(e) => setFormData({ ...formData, emailAddress: e.target.value })}
                    />
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleDialogClose} color="default">
                        Annuleren
                    </Button>
                    <Button onClick={handleSave} color="primary" disabled={!isFormValid()}>
                        Toepassen
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Save reminder after contact person changes (edit mode only) */}
            {props.isEditMode && changesCount > 0 && (
                <WarningMessage
                    key={changesCount}
                    id="contact-persons-save-reminder"
                    message={t('contactPersonsSaveReminder')}
                    info
                    suppressible
                    suppressDays={30}
                />
            )}

            {/* Delete Confirmation Dialog */}
            <Dialog
                open={deleteDialogOpen}
                onClose={() => setDeleteDialogOpen(false)}
            >
                <DialogTitle>Verwijderen</DialogTitle>
                <DialogContent>
                    <DialogContentText>
                        {t('contactPersonDeleteConfirm')}
                    </DialogContentText>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setDeleteDialogOpen(false)} color="default">
                        Annuleren
                    </Button>
                    <Button onClick={handleDeleteConfirm} color="secondary" startIcon={<DeleteIcon />}>
                        Verwijderen
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
};

const useStyles = makeStyles((theme: Theme) => ({
    root: {
        marginTop: theme.spacing(2),
        marginBottom: theme.spacing(2)
    },
    listItem: {
        border: '1px solid #e0e0e0',
        borderRadius: 4,
        marginBottom: theme.spacing(1)
    },
    addButton: {
        marginTop: theme.spacing(1)
    }
}));
